/**
 * Pha E trên web quầy (docs/plans/pha-e-giu-khach.md): thẻ "Chăm khách" (sinh nhật, lâu chưa quay
 * lại, gói sắp hết hạn — gọi / mở chat Zalo bằng tay, ước tính chi phí nếu gửi tin), thẻ "Chờ chỗ"
 * (khách chờ khe, có chỗ tính lúc đọc) và thẻ Cấu hình "Điểm thưởng" (ADR-051).
 * Gửi tin tự động bị khoá cho tới khi tiệm có mẫu Zalo được duyệt (ADR-052) — màn hình nói rõ.
 */
import { Cake, CalendarClock, Clock, Hourglass, MessageCircle, Package, Phone } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useState } from 'react'
import {
  type Api,
  type DanhSachChamKhach,
  type DongCho,
  type DongDenHan,
  type QuyTacDiem,
  tenKhach,
  tienVnd,
} from './api.ts'
import { taiCoHuy } from './chi-nhanh-dang-dung.ts'
import { useChiNhanh } from './chi-nhanh-dang-dung.tsx'
import { loiCua, ngayViet, OTien } from './GoiVi.tsx'
import { C } from './text/cham-khach.ts'
import { Rong, ThongBao, Xuong } from './ui.tsx'

/** "+84901234567" → "0901234567" để mở chat Zalo (zalo.me/<số>) và gọi điện. */
function soNoiDia(phone: string): string {
  return phone.startsWith('+84') ? `0${phone.slice(3)}` : phone
}

/** Số đã bị che (không có quyền xem) thì không có nút gọi / chat. */
function soDayDu(phone: string | null): phone is string {
  return phone !== null && !phone.includes('•') && !phone.includes('*')
}

function HanhDongLienHe({ phone }: { phone: string | null }) {
  if (!soDayDu(phone)) return null
  const so = soNoiDia(phone)
  return (
    <span className="hang-lien-he">
      <a className="nut nut-vien nut-icon" href={`tel:${so}`} aria-label={C.goi}>
        <Phone size={18} />
      </a>
      <a
        className="nut nut-vien nut-icon"
        href={`https://zalo.me/${so}`}
        target="_blank"
        rel="noreferrer"
        aria-label={C.chatZalo}
      >
        <MessageCircle size={18} />
      </a>
    </span>
  )
}

/** "lần cuối 27/09/2026": phần chữ tự xuống dòng, chỉ NGÀY giữ liền (không bị cắt trong cột hẹp ở 320px). */
function MocNgay({ moc }: { moc: { truoc: string; ngay: string } }) {
  return (
    <>
      {moc.truoc}
      <span className="khong-be">{moc.ngay}</span>
    </>
  )
}

/** "HH:MM" của phút-từ-nửa-đêm. */
function gioPhutChuoi(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

// ------------------------------------------------------------------ Chăm khách

/** Icon trạng thái rỗng theo từng danh sách (ui-critic v16–17: một bong bóng chat cho mọi thứ). */
const ICON_RONG: Record<DanhSachChamKhach['loai'], ReactNode> = {
  'sinh-nhat': <Cake size={26} />,
  'lau-chua-quay-lai': <Clock size={26} />,
  'goi-sap-het': <Package size={26} />,
}

type Muc = 'den-han' | DanhSachChamKhach['loai']

const LOAI: { ma: Muc; ten: string }[] = [
  // N3: đứng đầu — danh sách quầy làm mỗi ngày (chu kỳ theo dịch vụ); 3 mục cũ là dự phòng.
  { ma: 'den-han', ten: C.denHan },
  { ma: 'sinh-nhat', ten: C.sinhNhat },
  { ma: 'lau-chua-quay-lai', ten: C.lauChuaQuayLai },
  { ma: 'goi-sap-het', ten: C.goiSapHet },
]

export function TheChamKhach({ api, onMoDichVu }: { api: Api; onMoDichVu?: () => void }) {
  const [muc, setMuc] = useState<Muc>('den-han')
  return (
    <>
      <div className="phan-doan phan-doan-deu phan-doan-phu" role="tablist" aria-label={C.tieuDe}>
        {LOAI.map((l) => (
          <button
            key={l.ma}
            type="button"
            role="tab"
            aria-selected={muc === l.ma}
            className={muc === l.ma ? 'dang-chon' : ''}
            onClick={() => setMuc(l.ma)}
          >
            {l.ten}
          </button>
        ))}
      </div>
      {muc === 'den-han' ? (
        <DsDenHan api={api} {...(onMoDichVu === undefined ? {} : { onMoDichVu })} />
      ) : (
        <DsChamKhach api={api} loai={muc} />
      )}
    </>
  )
}

/** N3: khách đến hạn quay lại — chép lời mời soạn sẵn rồi mở chat Zalo; "Đã liên hệ" ẩn 14 ngày. */
function DsDenHan({ api, onMoDichVu }: { api: Api; onMoDichVu?: () => void }) {
  const [ds, setDs] = useState<DongDenHan[] | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [bao, setBao] = useState<string | null>(null)
  useEffect(() => {
    void api
      .denHan()
      .then((r) => setDs(r.khach))
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api])

  async function chep(k: DongDenHan) {
    if (k.loiMoi === null) return
    setLoi(null)
    // Mở cửa sổ NGAY trong lần bấm (trình duyệt chặn popup mở sau một `await`); cắt `opener` để trang
    // ngoài không điều hướng ngược được tab quầy (tabnabbing).
    const cua = k.sdtZalo !== null ? window.open('about:blank', '_blank') : null
    if (cua !== null) cua.opener = null
    let daChep = true
    try {
      await navigator.clipboard.writeText(k.loiMoi)
    } catch {
      daChep = false
    }
    if (cua !== null && k.sdtZalo !== null) cua.location.href = `https://zalo.me/${k.sdtZalo}`
    setBao(daChep ? C.denHanDaChep : C.denHanChepLoi)
  }

  async function daLienHe(k: DongDenHan) {
    setLoi(null)
    try {
      await api.daLienHeQuayLai(k.customerId, 'zalo_tay')
      setDs((cu) => (cu ?? []).filter((x) => x.customerId !== k.customerId))
      setBao(C.denHanDaAn(tenKhach(k.ten)))
    } catch (e) {
      setLoi(loiCua(e))
    }
  }

  if (ds === null) return loi !== null ? <ThongBao loai="loi">{loi}</ThongBao> : <Xuong cao={160} />
  return (
    <>
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {ds.length === 0 ? (
        <div className="the">
          <Rong
            icon={<CalendarClock size={26} />}
            tieuDe={C.denHanTrong}
            moTa={C.denHanTrongPhu}
            {...(onMoDichVu === undefined
              ? {}
              : {
                  hanhDong: (
                    <button type="button" className="nut nut-vien" onClick={onMoDichVu}>
                      {C.denHanSuaChuKy}
                    </button>
                  ),
                })}
          />
        </div>
      ) : (
        <>
          <section className="the the-dem so-to">
            <span className="nhan-muc">{C.denHanMoTa}</span>
            <b>{C.soKhach(ds.length)}</b>
          </section>
          <ul className="the ds-hoa-don">
            {ds.map((k) => (
              <li key={k.customerId} className="dong-cham dong-den-han">
                <span className="chu">
                  <b>{tenKhach(k.ten)}</b>
                  <span>{C.denHanDong(k.dichVu, ngayViet(k.lanCuoi), ngayViet(k.denHan))}</span>
                  {!k.nhanTin && <span className="chu-phu">{C.denHanChuaDongYTin}</span>}
                </span>
                <span className="hang-lien-he">
                  {k.loiMoi !== null && (
                    <button
                      type="button"
                      className="nut nut-chinh nut-nho"
                      onClick={() => void chep(k)}
                    >
                      {k.sdtZalo !== null ? C.denHanChepMoZalo : C.denHanChep}
                    </button>
                  )}
                  {soDayDu(k.sdt) && (
                    <a
                      className="nut nut-vien nut-icon"
                      href={`tel:${soNoiDia(k.sdt)}`}
                      aria-label={C.goi}
                    >
                      <Phone size={18} />
                    </a>
                  )}
                  <button
                    type="button"
                    className="nut nut-vien nut-nho"
                    onClick={() => void daLienHe(k)}
                  >
                    {C.denHanDaLienHe}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

function DsChamKhach({ api, loai }: { api: Api; loai: DanhSachChamKhach['loai'] }) {
  const [soNgay, setSoNgay] = useState(60)
  const [ds, setDs] = useState<DanhSachChamKhach | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  useEffect(() => {
    setDs(null)
    setLoi(null)
    void api
      .chamKhach(loai, soNgay)
      .then(setDs)
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api, loai, soNgay])

  return (
    <>
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {ds === null ? (
        loi === null && <Xuong cao={160} />
      ) : (
        <>
          {loai === 'lau-chua-quay-lai' && (
            <fieldset className="hang-chip khung-trong chip-nho" aria-label={C.soNgayNhan}>
              <span className="nhan-chip">{C.chuaQuayLaiHon}</span>
              {[30, 60, 90].map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-pressed={soNgay === n}
                  className={`chip ${soNgay === n ? 'dang-chon' : ''}`}
                  onClick={() => setSoNgay(n)}
                >
                  {C.soNgay(n)}
                </button>
              ))}
            </fieldset>
          )}
          {ds.soNguoi > 0 && (
            <section className="the the-dem so-to">
              <span className="nhan-muc">{C.moTa[loai]}</span>
              <b>{C.soKhach(ds.soNguoi)}</b>
              <span className="chu-phu">
                {ds.tuDongGui ? C.daTuDong : C.uocTinh(ds.soNhanTin, tienVnd(ds.uocTinhVnd))}
              </span>
            </section>
          )}
          {!ds.tuDongGui && ds.soNguoi > 0 && (
            <ThongBao loai="canh-bao">
              {loai === 'goi-sap-het' ? C.chuaTuDongGoi : C.chuaTuDong}
            </ThongBao>
          )}
          {ds.khach.length === 0 ? (
            <div className="the">
              <Rong icon={ICON_RONG[loai]} tieuDe={C.trong[loai]} moTa={C.trongPhu[loai]} />
            </div>
          ) : (
            <ul className="the ds-hoa-don">
              {ds.khach.map((k) => (
                <li key={k.id} className="dong-cham">
                  <span className="chu">
                    <b>{tenKhach(k.ten)}</b>
                    <span>
                      {k.moc !== null && <MocNgay moc={C.moc[loai](ngayViet(k.moc))} />}
                      {k.nhanTin && (
                        <>
                          {' · '}
                          <span className="khong-be">{C.dongYNhan}</span>
                        </>
                      )}
                    </span>
                  </span>
                  <HanhDongLienHe phone={k.phone} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  )
}

// ------------------------------------------------------------------ Chờ chỗ

export function TheChoCho({ api }: { api: Api }) {
  const [ds, setDs] = useState<DongCho[] | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  // ADR-101: chi nhánh đang dùng chung (nút chọn nằm ở đầu trang Khách) — không còn ô chọn riêng ở đây.
  const { ds: cn, id: branchId } = useChiNhanh()
  useEffect(() => {
    if (cn !== null && cn.length === 0) setDs([])
  }, [cn])
  useEffect(() => {
    if (branchId === '') return
    setLoi(null)
    setDs(null)
    // Kết quả của chi nhánh cũ về muộn bị bỏ (vòng 1 A) — không đè danh sách chi nhánh mới.
    return taiCoHuy(
      api.choCho(branchId),
      (r) => setDs(r.cho),
      (e) => setLoi(loiCua(e)),
    )
  }, [api, branchId])
  if (loi !== null) return <ThongBao loai="loi">{loi}</ThongBao>
  if (ds === null) return <Xuong cao={120} />
  if (ds.length === 0) {
    return (
      <>
        <div className="the">
          <Rong icon={<Hourglass size={26} />} tieuDe={C.khongAiCho} moTa={C.khongAiChoPhu} />
        </div>
      </>
    )
  }
  return (
    <>
      <ul className="the ds-hoa-don">
        {ds.map((w) => (
          <li key={w.id} className="dong-cham">
            <span className="chu">
              <b>
                {tenKhach(w.khach)}
                {!soDayDu(w.phone) && <span className="nhan-khong-so">{C.khongSo}</span>}
              </b>
              <span>
                {ngayViet(w.ngay)} · {w.dichVu}
                {w.coCho.length === 0 && <span className="khong-be">{` · ${C.chuaCoCho}`}</span>}
              </span>
              {w.coCho.length > 0 && (
                <b className="chu-tang">{C.coCho(w.coCho.map(gioPhutChuoi).join(', '))}</b>
              )}
              {w.coCho.length > 0 && <span>{soDayDu(w.phone) ? C.coChoGoi : C.coChoTuDat}</span>}
            </span>
            {soDayDu(w.phone) && <HanhDongLienHe phone={w.phone} />}
          </li>
        ))}
      </ul>
    </>
  )
}

// ------------------------------------------------------------------ Cấu hình: điểm thưởng

export function TheDiemCauHinh({ api }: { api: Api }) {
  const [q, setQ] = useState<QuyTacDiem | null>(null)
  const [bao, setBao] = useState<string | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  useEffect(() => {
    void api
      .quyTacDiem()
      .then(setQ)
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api])
  async function luu() {
    if (q === null) return
    try {
      setQ(await api.luuQuyTacDiem(q))
      setBao(C.daLuuDiem)
    } catch (e) {
      setLoi(loiCua(e))
    }
  }
  const hopLe =
    q !== null &&
    q.earnPerVnd.endsWith('000') &&
    q.pointValueVnd.endsWith('000') &&
    q.earnPerVnd !== '0' &&
    q.pointValueVnd !== '0' &&
    BigInt(q.pointValueVnd) * 10000n <= BigInt(q.earnPerVnd) * 2000n
  // Tỉ lệ hoàn (basis point → "%"), chỉ để chủ tiệm hiểu quy tắc; máy chủ chặn trên 20%.
  const hoanBp =
    q === null || q.earnPerVnd === '' || q.earnPerVnd === '0' || q.pointValueVnd === ''
      ? null
      : (BigInt(q.pointValueVnd) * 10000n) / BigInt(q.earnPerVnd)
  return (
    <section className="the the-dem">
      <h2>{C.diemTieuDe}</h2>
      <p className="chu-phu">{C.diemGoiY}</p>
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {q === null ? (
        <Xuong cao={80} />
      ) : (
        <>
          <label className="cong-tac-dong">
            <span className="chu">
              <b>{C.batDiem}</b>
            </span>
            <input
              type="checkbox"
              checked={q.batDau}
              onChange={(e) => setQ({ ...q, batDau: e.target.checked })}
            />
          </label>
          <div className="luoi-2">
            <div className="truong">
              <label className="nhan" htmlFor="diem-tich">
                {C.tichMoi}
              </label>
              <OTien
                id="diem-tich"
                giaTri={q.earnPerVnd}
                onDoi={(v) => setQ({ ...q, earnPerVnd: v })}
              />
            </div>
            <div className="truong">
              <label className="nhan" htmlFor="diem-gia">
                {C.motDiem}
              </label>
              <OTien
                id="diem-gia"
                giaTri={q.pointValueVnd}
                onDoi={(v) => setQ({ ...q, pointValueVnd: v })}
              />
            </div>
          </div>
          <p className="chu-phu">
            {C.viDuDiem(tienVnd(q.earnPerVnd || '0'), tienVnd(q.pointValueVnd || '0'))}
            {hoanBp !== null &&
              ` ${C.hoan(
                `${hoanBp / 100n}${
                  hoanBp % 100n === 0n
                    ? ''
                    : `,${String(hoanBp % 100n)
                        .padStart(2, '0')
                        .replace(/0$/, '')}`
                }`,
              )}`}
          </p>
          {hoanBp !== null && hoanBp > 2000n && <p className="chu-phu chu-loi">{C.hoanQuaCao}</p>}
          <button
            type="button"
            className="nut nut-chinh nut-nho"
            style={{ marginTop: 12 }}
            disabled={!hopLe}
            onClick={() => void luu()}
          >
            {C.luuDiem}
          </button>
        </>
      )}
    </section>
  )
}
